/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import {
  GET_PASSWORD_POLICIES_START,
  GET_PASSWORD_POLICIES_SUCCESS,
  GET_PASSWORD_POLICIES_FAILURE,
  GET_PASSWORD_POLICY_DETAILS_START,
  GET_PASSWORD_POLICY_DETAILS_SUCCESS,
  GET_PASSWORD_POLICY_DETAILS_FAILURE,
  GET_PASSWORD_POLICIES_RULE_START,
  GET_PASSWORD_POLICIES_RULE_SUCCESS,
  GET_PASSWORD_POLICIES_RULE_FAILURE,
  FETCH_UPDATE_DATA_FORM_VALUES
} from './types';
import { PasswordPolicyModel, PasswordPolicyDetails, IPasswordFormPolicy } from '../../models/PasswordPolicy/PasswordPolicy';
import { formValuesPasswordPolicyDefault } from '../../components/PasswordPolicies/constants';
import { NO_LIMIT } from '../../components/PasswordPolicies/PasswordPolicyTable/PasswordPolicyDetails/constants';

export const initialState: {
  entities: PasswordPolicyModel[];
  isLoading: boolean;
  formValues: IPasswordFormPolicy;
  error: string | undefined;
  detailsById: {
    [policyId: string]: { policyDetails: PasswordPolicyDetails; isLoading: boolean; error: string | undefined };
  };
} = {
  entities: [],
  formValues: {
    policyName: {
      value: '',
      error: false,
      errorHelperText: ''
    },
    minLength: {
      value: '',
      error: false,
      errorHelperText: ''
    },
    maxLength: {
      value: '',
      error: false,
      errorHelperText: ''
    },
    expiryDuration: {
      value: '',
      error: false,
      errorHelperText: ''
    },
    maxConsecutiveLetters: {
      value: NO_LIMIT,
      error: false,
      errorHelperText: ''
    },
    oldPasswords: {
      value: '',
      error: false,
      errorHelperText: ''
    },
    includeNumbers: false,
    passwordHistoryEnabled: true,
    includeSpecialCharacters: false,
    lettersInMixedCase: false,
    excludeCommonUnsafePassword: false,
    excludeUserProfileInformation: false
  },
  isLoading: true,
  error: undefined,
  detailsById: {},
};

export const passwordPoliciesReducer = (state = initialState, action: any) => {
  switch (action.type) {
    case GET_PASSWORD_POLICIES_START:
      return {
        ...state,
        isLoading: true,
        error: undefined
      };
    case GET_PASSWORD_POLICIES_SUCCESS:
      return {
        ...state,
        isLoading: false,
        entities: action.passwordPolicies,
        error: undefined
      };
    case GET_PASSWORD_POLICIES_FAILURE:
      return {
        ...state,
        isLoading: false,
        entities: [],
        error: action.error
      };
    case GET_PASSWORD_POLICY_DETAILS_START:
      return {
        ...state,
        detailsById: {
          ...state.detailsById,
          [action.passwordPolicyId]: {
            ...state.detailsById[action.passwordPolicyId],
            isLoading: true,
            policyDetails: undefined,
            error: undefined
          }
        }
      };
    case GET_PASSWORD_POLICY_DETAILS_SUCCESS:
      return {
        ...state,
        detailsById: {
          ...state.detailsById,
          [action.passwordPolicyId]: {
            ...state.detailsById[action.passwordPolicyId],
            isLoading: false,
            policyDetails: action.policyDetails,
            error: undefined
          }
        }
      };
    case GET_PASSWORD_POLICY_DETAILS_FAILURE:
      return {
        ...state,
        detailsById: {
          ...state.detailsById,
          [action.passwordPolicyId]: {
            ...state.detailsById[action.passwordPolicyId],
            isLoading: false,
            policyDetails: undefined,
            error: action.error
          }
        }
      };
    case FETCH_UPDATE_DATA_FORM_VALUES:
      return {
        ...state,
        formValues: { ...state.formValues, ...action.data },
        isLoading: false
      };
    case GET_PASSWORD_POLICIES_RULE_START:
      return {
        ...state
      };
    case GET_PASSWORD_POLICIES_RULE_SUCCESS:
      const data = action.data || {};
      const object = {
        ...formValuesPasswordPolicyDefault,
        policyName: {
          ...formValuesPasswordPolicyDefault.policyName,
          value: data.name ?? formValuesPasswordPolicyDefault.policyName.value
        },
        minLength: {
          ...formValuesPasswordPolicyDefault.minLength,
          value: data.minLength ?? formValuesPasswordPolicyDefault.minLength.value
        },
        maxLength: {
          ...formValuesPasswordPolicyDefault.maxLength,
          value: data.maxLength ?? formValuesPasswordPolicyDefault.maxLength.value
        },
        expiryDuration: {
          ...formValuesPasswordPolicyDefault.expiryDuration,
          value: data.expiryDuration ?? formValuesPasswordPolicyDefault.expiryDuration.value
        },
        includeNumbers: data.includeNumbers ?? formValuesPasswordPolicyDefault.includeNumbers,
        includeSpecialCharacters: data.includeSpecialCharacters ?? formValuesPasswordPolicyDefault.includeSpecialCharacters,
        lettersInMixedCase: data.lettersInMixedCase ?? formValuesPasswordPolicyDefault.lettersInMixedCase,
        excludeCommonUnsafePassword: data.excludePasswordsInDictionary ?? formValuesPasswordPolicyDefault.excludeCommonUnsafePassword,
        excludeUserProfileInformation: data.excludeUserProfileInfoInPassword ?? formValuesPasswordPolicyDefault.excludeUserProfileInformation,
        maxConsecutiveLetters: {
          ...formValuesPasswordPolicyDefault.maxConsecutiveLetters,
          value: data.maxConsecutiveLetters ?? formValuesPasswordPolicyDefault.maxConsecutiveLetters.value
        },
        passwordHistoryEnabled: data.passwordHistoryEnabled ?? formValuesPasswordPolicyDefault.passwordHistoryEnabled,
        oldPasswords: {
          ...formValuesPasswordPolicyDefault.oldPasswords,
          value: data.oldPasswords ?? formValuesPasswordPolicyDefault.oldPasswords.value
        }
      };
      return {
        ...state,
        formValues: object,
        isLoading: false,
        error: undefined
      };
    case GET_PASSWORD_POLICIES_RULE_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.error
      };
    default:
      return state;
  }
};
